import { useHouseholdChatContext } from '../contexts/householdChat.context';
import {
  HouseholdChatPersonasQuery,
  useHouseholdChatPersonasQuery,
} from '../graphql/HouseholdChat.generated';

/** One selectable persona from the server's catalog, with display metadata. */
export type HouseholdChatPersonaOption =
  HouseholdChatPersonasQuery['householdChatPersonas'][number];

/**
 * The catalog entry for the persona applied to the next message, or null when
 * the chat runs the default (no-persona) behavior.
 *
 * Null also when a persona is selected but its catalog entry can't be found
 * (the query failed, or the persona was removed server-side) — callers that
 * must render something while a persona is applied (the chip, the composer
 * placeholder) supply their own fallback off `chatPersona`.
 */
export function useSelectedHouseholdChatPersona(): HouseholdChatPersonaOption | null {
  const { chatPersona } = useHouseholdChatContext();

  // The selector has already run this query whenever a persona can be
  // selected, so this usually reads straight from the Apollo cache. Errors
  // are reported where the catalog is loaded (the selector); here a missing
  // catalog just means no metadata.
  const { data } = useHouseholdChatPersonasQuery({ skip: !chatPersona });

  if (!chatPersona) {
    return null;
  }
  return (
    data?.householdChatPersonas.find(
      (option) => option.persona === chatPersona
    ) ?? null
  );
}
